import { Divider } from '@chakra-ui/layout';
import { IconMagnifyingGlass } from 'central-icons/IconMagnifyingGlass';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { Button, Checkbox, CheckboxGroup, Dialog, Input } from '@revelio/ui';

import { SelectionListControls } from '../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import modalStyles from '../../components/Modal/themedModal.module.css';
import type { SecondaryFilterConfig } from '../../components/PageConfigProvider/page-reducer';
import { isTaxonomyFilterId } from '../constants/filter-constants';
import { useFilterContext } from '../contexts/FilterProvider';
import { ALL_FILTER_DEFINITIONS } from '../engine/filterRegistry';
import { FilterId, FilterType } from '../types/filters';
import styles from './filter-select-modal.module.css';

/**
 * Default filters available for custom pages.
 * These are the standard filters that users typically need.
 * Excludes screener-specific filters (MULTI_*, company detail filters, etc.)
 */
export const DEFAULT_AVAILABLE_FILTER_IDS: FilterId[] = [
  // Primary entity filters
  FilterId.COMPANY,
  FilterId.INDUSTRY,
  FilterId.GEOGRAPHY,
  FilterId.ROLE,
  // Dimension filters
  FilterId.SENIORITY,
  FilterId.SKILL,
  FilterId.CUSTOM_SKILL,
  FilterId.ACTIVITY,
  FilterId.EDUCATION,
  FilterId.ETHNICITY,
  FilterId.GENDER,
  FilterId.TITLE,
  // Time filters
  FilterId.DATE_RANGE,
  FilterId.SNAPSHOT_DATE,
  // Data adjustment filters
  FilterId.CONTINGENT_WORKERS,
  FilterId.DATA_ADJUSTMENTS,
  // Sentiment filters
  FilterId.OVERALL_RATING,
  // Postings filters
  FilterId.POSTINGS_SOURCE,
  FilterId.METRIC_MODE,
  FilterId.KEYWORDS,
];

interface FilterOption {
  id: FilterId;
  name: string;
}

/**
 * Get all available filter options for the selection modal.
 * Filters out internal filters and MULTI filters (which are screener-specific).
 */
const getAvailableFilterOptions = (
  excludedFilterIds: readonly FilterId[]
): FilterOption[] => {
  return Object.entries(ALL_FILTER_DEFINITIONS)
    .filter(([id, def]) => {
      const filterId = id as FilterId;
      // Exclude internal taxonomy filters
      if (isTaxonomyFilterId(filterId)) return false;
      if (excludedFilterIds.includes(filterId)) return false;
      // Exclude MULTI filters (screener/TD specific)
      if (def.type === FilterType.MULTI) return false;
      // Exclude GROUP filters (they're containers, not actual filters)
      if (def.type === FilterType.GROUP) return false;
      return true;
    })
    .map(([id, def]) => ({
      id: id as FilterId,
      name: def.name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

interface FilterSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFilters: SecondaryFilterConfig[];
  onSave: (filters: SecondaryFilterConfig[]) => void;
}

export const FilterSelectModal = ({
  isOpen,
  onClose,
  currentFilters,
  onSave,
}: FilterSelectModalProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilterIds, setSelectedFilterIds] = useState<FilterId[]>([]);
  const { activityTaxonomyEnabled: isActivityTaxonomyEnabled } =
    useFilterContext();

  const allFilterOptions = getAvailableFilterOptions(
    isActivityTaxonomyEnabled ? [] : [FilterId.ACTIVITY]
  );

  // Initialize temporary selection only when modal opens (not on every render).
  // Intentionally omit currentFilters so we don't reset user's edits when parent re-renders.
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (!isOpen) return;
    const currentIds = currentFilters.map((f) => f.id);
    const nextIds =
      currentIds.length === 0
        ? DEFAULT_AVAILABLE_FILTER_IDS.filter((id) =>
            allFilterOptions.some((opt) => opt.id === id)
          )
        : currentIds;
    setSelectedFilterIds(nextIds);
    setSearchTerm('');
  }, [isOpen]);

  const filteredOptions = allFilterOptions.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectionChange = (values: string[]) => {
    // Maintain the order based on allFilterOptions
    const sortedValues = allFilterOptions
      .filter((opt) => values.includes(opt.id))
      .map((opt) => opt.id);
    setSelectedFilterIds(sortedValues);
  };

  const handleSave = () => {
    const filters: SecondaryFilterConfig[] = selectedFilterIds.map((id) => ({
      id,
    }));
    onSave(filters);
    onClose();
  };

  const handleSelectAll = () => {
    setSelectedFilterIds(allFilterOptions.map((opt) => opt.id));
  };

  const handleSelectNone = () => {
    setSelectedFilterIds([]);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Manage Filters"
      size="lg"
      className={modalStyles.content}
      footer={
        <SelectionListControls
          onClose={onClose}
          onSubmit={handleSave}
          submitText="Save"
        />
      }
    >
      <Divider orientation="horizontal" className={styles.headerRule} />
      <Input
        placeholder="Search filters..."
        value={searchTerm}
        onChange={handleSearchChange}
        wrapperClassName={modalStyles.searchField}
        endSlot={
          <IconMagnifyingGlass size={16} className={modalStyles.muted} />
        }
      />

      <div className={styles.bulkActions}>
        <Button
          variant="link"
          size="xs"
          color="lightBlue"
          onClick={handleSelectAll}
        >
          Select All
        </Button>
        <Button
          variant="link"
          size="xs"
          color="lightBlue"
          onClick={handleSelectNone}
        >
          Select None
        </Button>
      </div>

      <div className={classNames(modalStyles.scrollArea, styles.listScroll)}>
        <CheckboxGroup
          value={selectedFilterIds}
          onChange={handleSelectionChange}
        >
          <div className={styles.checkboxList}>
            {filteredOptions.map((option) => (
              <Checkbox
                key={option.id}
                value={option.id}
                data-testid={`${option.id}-checkbox`}
                className={styles.checkbox}
                labelSize="md"
              >
                {option.name}
              </Checkbox>
            ))}
          </div>
        </CheckboxGroup>
      </div>
    </Dialog>
  );
};
